import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const MONTH_OPTIONS = [
  { value: 1, label: '01' },
  { value: 2, label: '02' },
  { value: 3, label: '03' },
  { value: 4, label: '04' },
  { value: 5, label: '05' },
  { value: 6, label: '06' },
  { value: 7, label: '07' },
  { value: 8, label: '08' },
  { value: 9, label: '09' },
  { value: 10, label: '10' },
  { value: 11, label: '11' },
  { value: 12, label: '12' },
];

const VIEW_OPTIONS = [
  { value: 'MENSAL', label: 'Mensal' },
  { value: 'ANUAL', label: 'Anual' },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toPercent = (value) =>
  `${Math.max(0, toNumber(value)).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

const clampPercent = (value) => Math.min(100, Math.max(0, toNumber(value)));

const formatMoney = (value) =>
  toNumber(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatKwh = (value) =>
  `${toNumber(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kWh`;

const progressColorClass = (variant) => {
  if (variant === 'success') return 'bg-emerald-500';
  if (variant === 'warning') return 'bg-amber-500';
  if (variant === 'danger') return 'bg-rose-500';
  return 'bg-sky-500';
};

const normalizeView = (value, fallback = 'MENSAL') => {
  const text = String(value || '').trim().toUpperCase();
  if (text === 'ANUAL') return 'ANUAL';
  if (text === 'MENSAL') return 'MENSAL';
  return fallback;
};

const aggregateAnnualSummary = (year, monthlySummaries = []) => {
  const months = MONTH_OPTIONS.map(({ value, label }) => {
    const monthData = monthlySummaries.find((item) => Number(item?.competencia?.mes) === value) || {};
    const op = monthData?.metricas?.operacional || {};
    const fin = monthData?.metricas?.financeiro || {};
    return {
      mes: value,
      label,
      operacional: {
        total_usinas: toNumber(op.total_usinas),
        total_ucs_cadastradas: toNumber(op.total_ucs_cadastradas),
        total_ucs_com_fatura: toNumber(op.total_ucs_com_fatura),
        total_usinas_com_fatura: toNumber(op.total_usinas_com_fatura),
        kwh_gerado: toNumber(op.kwh_gerado),
        kwh_consumido: toNumber(op.kwh_consumido),
        kwh_saldo_creditos: toNumber(op.kwh_saldo_creditos),
        consumo_medio_uc: toNumber(op.consumo_medio_uc),
      },
      financeiro: {
        valor_gerado: toNumber(fin.valor_gerado),
        valor_recebido: toNumber(fin.valor_recebido),
        valor_em_aberto: toNumber(fin.valor_em_aberto),
        valor_em_atraso: toNumber(fin.valor_em_atraso),
        custo_enel: toNumber(fin.custo_enel),
        lucro_bruto_mes: toNumber(fin.lucro_bruto_mes),
      },
    };
  });

  let totalUsinas = 0;
  let totalUcs = 0;
  let totalUcsComFaturaMax = 0;
  let totalUsinasComFaturaMax = 0;
  let kwhGerado = 0;
  let kwhConsumido = 0;
  let kwhSaldoCreditos = 0;
  let consumoMedioUcSoma = 0;
  let consumoMedioUcCount = 0;
  let valorGerado = 0;
  let valorRecebido = 0;
  let valorEmAberto = 0;
  let valorEmAtraso = 0;
  let custoEnel = 0;

  months.forEach((month) => {
    totalUsinas = Math.max(totalUsinas, month.operacional.total_usinas);
    totalUcs = Math.max(totalUcs, month.operacional.total_ucs_cadastradas);
    totalUcsComFaturaMax = Math.max(totalUcsComFaturaMax, month.operacional.total_ucs_com_fatura);
    totalUsinasComFaturaMax = Math.max(totalUsinasComFaturaMax, month.operacional.total_usinas_com_fatura);

    kwhGerado += month.operacional.kwh_gerado;
    kwhConsumido += month.operacional.kwh_consumido;
    kwhSaldoCreditos = month.operacional.kwh_saldo_creditos;

    if (month.operacional.consumo_medio_uc > 0) {
      consumoMedioUcSoma += month.operacional.consumo_medio_uc;
      consumoMedioUcCount += 1;
    }

    valorGerado += month.financeiro.valor_gerado;
    valorRecebido += month.financeiro.valor_recebido;
    valorEmAberto += month.financeiro.valor_em_aberto;
    valorEmAtraso += month.financeiro.valor_em_atraso;
    custoEnel += month.financeiro.custo_enel;
  });

  const consumoMedioUc = consumoMedioUcCount > 0 ? consumoMedioUcSoma / consumoMedioUcCount : 0;
  const lucroBrutoAno = valorGerado - custoEnel;
  const kwhSaldoPotencial = Math.max(0, kwhGerado - kwhConsumido);

  const recentRef = monthlySummaries[0];
  const competenciasRecentes = Array.isArray(recentRef?.competencias_recentes) ? recentRef.competencias_recentes : [];

  return {
    visao: 'ANUAL',
    competencia: {
      mes: null,
      ano: year,
      label: `Ano ${year}`,
    },
    metricas: {
      operacional: {
        total_usinas: totalUsinas,
        total_ucs_cadastradas: totalUcs,
        total_ucs_com_fatura: totalUcsComFaturaMax,
        total_usinas_com_fatura: totalUsinasComFaturaMax,
        kwh_gerado: kwhGerado,
        kwh_consumido: kwhConsumido,
        kwh_saldo_potencial: kwhSaldoPotencial,
        kwh_saldo_creditos: kwhSaldoCreditos,
        consumo_medio_uc: consumoMedioUc,
      },
      financeiro: {
        valor_gerado: valorGerado,
        valor_recebido: valorRecebido,
        valor_em_aberto: valorEmAberto,
        valor_em_atraso: valorEmAtraso,
        custo_enel: custoEnel,
        lucro_bruto_mes: lucroBrutoAno,
      },
    },
    competencias_recentes: competenciasRecentes,
    serie_mensal: months,
  };
};

const ProgressCard = ({ title, value, subtitle, percent, variant = 'info' }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
    <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    <p className="mt-1 text-xs text-slate-600">{subtitle}</p>
    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${progressColorClass(variant)}`} style={{ width: `${clampPercent(percent)}%` }} />
    </div>
  </div>
);

const SolarDashboardPage = () => {
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear - 3; year <= currentYear + 1; year += 1) years.push(year);
    return years.reverse();
  }, [currentYear]);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    visao: 'MENSAL',
    competencia_mes: '',
    competencia_ano: '',
  });
  const lastRequestIdRef = useRef(0);

  const loadSummary = async (override = null) => {
    const nextFilters = override || filters;
    const view = normalizeView(nextFilters.visao, 'MENSAL');
    const parsedYear = Number(nextFilters.competencia_ano);
    const targetYear = Number.isInteger(parsedYear) && parsedYear >= 2000 && parsedYear <= 2100 ? parsedYear : currentYear;
    const requestId = lastRequestIdRef.current + 1;
    lastRequestIdRef.current = requestId;

    setLoading(true);
    try {
      if (view === 'ANUAL') {
        const annualRequests = MONTH_OPTIONS.map((month) =>
          axios.get('/api/solar/dashboard/summary', {
            params: {
              competencia_mes: month.value,
              competencia_ano: targetYear,
            },
          })
        );
        const annualResponses = await Promise.all(annualRequests);
        const monthlySummaries = annualResponses.map((item) => item?.data).filter(Boolean);
        const annualSummary = aggregateAnnualSummary(targetYear, monthlySummaries);
        if (requestId !== lastRequestIdRef.current) return;
        setSummary(annualSummary);
        return;
      }

      const params = {};
      if (nextFilters.competencia_mes) params.competencia_mes = Number(nextFilters.competencia_mes);
      if (nextFilters.competencia_ano) params.competencia_ano = Number(nextFilters.competencia_ano);

      const { data } = await axios.get('/api/solar/dashboard/summary', { params });
      const normalized = {
        ...(data || {}),
        visao: 'MENSAL',
        serie_mensal: [],
      };
      if (requestId !== lastRequestIdRef.current) return;
      setSummary(normalized);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar dashboard solar');
    } finally {
      if (requestId === lastRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      loadSummary(filters);
    }, 120);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.visao, filters.competencia_mes, filters.competencia_ano]);

  const clearFilters = () => {
    setFilters((old) => ({
      ...old,
      competencia_mes: '',
      competencia_ano: '',
    }));
  };

  const view = normalizeView(summary?.visao || filters.visao, 'MENSAL');
  const isAnnual = view === 'ANUAL';
  const periodText = isAnnual ? 'no ano' : 'no periodo';

  const operacional = summary?.metricas?.operacional || {};
  const financeiro = summary?.metricas?.financeiro || {};
  const serieMensal = Array.isArray(summary?.serie_mensal) ? summary.serie_mensal : [];
  const competenciasRecentes = Array.isArray(summary?.competencias_recentes) ? summary.competencias_recentes : [];

  const kwhGerado = Math.max(0, toNumber(operacional.kwh_gerado));
  const kwhConsumido = Math.max(0, toNumber(operacional.kwh_consumido));
  const kwhSaldoCreditos = Math.max(0, toNumber(operacional.kwh_saldo_creditos));
  const valorGerado = Math.max(0, toNumber(financeiro.valor_gerado));
  const valorRecebido = Math.max(0, toNumber(financeiro.valor_recebido));
  const valorEmAberto = Math.max(0, toNumber(financeiro.valor_em_aberto));
  const valorEmAtraso = Math.max(0, toNumber(financeiro.valor_em_atraso));

  const coberturaEnergetica = kwhConsumido > 0 ? (kwhGerado / kwhConsumido) * 100 : 0;
  const taxaRecebimento = valorGerado > 0 ? (valorRecebido / valorGerado) * 100 : 0;
  const riscoAtraso = valorGerado > 0 ? (valorEmAtraso / valorGerado) * 100 : 0;
  const saldoCreditosRelativo = kwhConsumido > 0 ? (kwhSaldoCreditos / kwhConsumido) * 100 : 0;
  const maiorValorGeradoMensal = Math.max(1, ...serieMensal.map((item) => toNumber(item?.financeiro?.valor_gerado)));

  const cardsOperacao = [
    {
      label: 'Usinas ativas',
      value: toNumber(operacional.total_usinas).toLocaleString('pt-BR'),
      hint: `${toNumber(operacional.total_usinas_com_fatura).toLocaleString('pt-BR')} com leitura ${periodText}`,
      style: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    },
    {
      label: 'UCs cadastradas',
      value: toNumber(operacional.total_ucs_cadastradas).toLocaleString('pt-BR'),
      hint: `${toNumber(operacional.total_ucs_com_fatura).toLocaleString('pt-BR')} com leitura ${periodText}`,
      style: 'border-teal-200 bg-teal-50 text-teal-900',
    },
    {
      label: 'kWh gerado',
      value: formatKwh(kwhGerado),
      hint: `Energia disponivel ${periodText}`,
      style: 'border-amber-200 bg-amber-50 text-amber-900',
    },
    {
      label: 'kWh consumido',
      value: formatKwh(kwhConsumido),
      hint: `Consumo total ${periodText}`,
      style: 'border-sky-200 bg-sky-50 text-sky-900',
    },
  ];

  const cardsFinanceiro = [
    {
      label: 'Valor gerado',
      value: formatMoney(valorGerado),
      hint: `Total emitido ${periodText}`,
      style: 'border-slate-200 bg-slate-50 text-slate-900',
    },
    {
      label: 'Valor recebido',
      value: formatMoney(valorRecebido),
      hint: `Taxa de recebimento: ${toPercent(taxaRecebimento)}`,
      style: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    },
    {
      label: 'Em aberto',
      value: formatMoney(valorEmAberto),
      hint: `Saldo pendente ${periodText}`,
      style: 'border-amber-200 bg-amber-50 text-amber-900',
    },
    {
      label: 'Em atraso',
      value: formatMoney(valorEmAtraso),
      hint: `Risco sobre gerado: ${toPercent(riscoAtraso)}`,
      style: 'border-rose-200 bg-rose-50 text-rose-900',
    },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-emerald-300 bg-gradient-to-r from-emerald-700 via-teal-700 to-sky-700 p-6 text-white shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Solar - Dashboard Gerencial</h1>
            <p className="mt-1 text-emerald-50">
              Painel operacional e financeiro com leitura de energia, cobrancas e risco de recebimento
            </p>
          </div>
          <div className="rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-right">
            <p className="text-xs uppercase tracking-wide text-emerald-100">Periodo analisado</p>
            <p className="text-xl font-semibold">{summary?.competencia?.label || 'Auto'}</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-white/25 bg-white/10 p-3 backdrop-blur-sm">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-emerald-50 mb-1">Visao</label>
              <select
                className="w-full rounded-md border border-white/60 bg-white/95 px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-300"
                value={filters.visao}
                onChange={(e) =>
                  setFilters((old) => ({
                    ...old,
                    visao: normalizeView(e.target.value, 'MENSAL'),
                    competencia_mes: normalizeView(e.target.value, 'MENSAL') === 'ANUAL' ? '' : old.competencia_mes,
                  }))
                }
              >
                {VIEW_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-emerald-50 mb-1">Competencia Mes</label>
              <select
                className="w-full rounded-md border border-white/60 bg-white/95 px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-70"
                value={filters.competencia_mes}
                disabled={normalizeView(filters.visao, 'MENSAL') === 'ANUAL'}
                onChange={(e) => setFilters((old) => ({ ...old, competencia_mes: e.target.value }))}
              >
                <option value="">Auto</option>
                {MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-emerald-50 mb-1">Competencia Ano</label>
              <select
                className="w-full rounded-md border border-white/60 bg-white/95 px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-300"
                value={filters.competencia_ano}
                onChange={(e) => setFilters((old) => ({ ...old, competencia_ano: e.target.value }))}
              >
                <option value="">Auto</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex gap-2 items-end">
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={clearFilters} disabled={loading}>
                Limpar
              </button>
              {loading && <div className="loader h-5 w-5" />}
            </div>
            <div className="md:col-span-4">
              <p className="text-xs text-emerald-100 mb-1">Atualizacao automatica ao alterar os filtros</p>
              <p className="text-xs uppercase tracking-wide text-emerald-100">Competencias recentes</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {competenciasRecentes.length > 0 ? (
                  competenciasRecentes.slice(0, 6).map((item) => (
                    <span
                      key={`${item.competencia_ano}-${item.competencia_mes}`}
                      className="rounded-md border border-white/35 bg-white/15 px-2 py-0.5 text-xs text-white"
                    >
                      {String(item.competencia_mes).padStart(2, '0')}/{item.competencia_ano}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-emerald-100">Sem historico</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cardsOperacao.map((card) => (
          <div key={card.label} className={`rounded-xl border p-4 shadow-sm ${card.style}`}>
            <p className="text-xs uppercase tracking-wide opacity-80">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value}</p>
            <p className="mt-1 text-xs opacity-80">{card.hint}</p>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cardsFinanceiro.map((card) => (
          <div key={card.label} className={`rounded-xl border p-4 shadow-sm ${card.style}`}>
            <p className="text-xs uppercase tracking-wide opacity-80">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value}</p>
            <p className="mt-1 text-xs opacity-80">{card.hint}</p>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
        <ProgressCard
          title="Cobertura energetica"
          value={toPercent(coberturaEnergetica)}
          subtitle={`${formatKwh(kwhGerado)} gerado x ${formatKwh(kwhConsumido)} consumido`}
          percent={coberturaEnergetica}
          variant={coberturaEnergetica >= 100 ? 'success' : 'warning'}
        />
        <ProgressCard
          title="Taxa de recebimento"
          value={toPercent(taxaRecebimento)}
          subtitle={`${formatMoney(valorRecebido)} recebido sobre ${formatMoney(valorGerado)}`}
          percent={taxaRecebimento}
          variant={taxaRecebimento >= 85 ? 'success' : 'warning'}
        />
        <ProgressCard
          title="Risco de atraso"
          value={toPercent(riscoAtraso)}
          subtitle={`${formatMoney(valorEmAtraso)} com vencimento expirado`}
          percent={riscoAtraso}
          variant={riscoAtraso >= 20 ? 'danger' : 'info'}
        />
        <ProgressCard
          title="Credito sobre consumo"
          value={toPercent(saldoCreditosRelativo)}
          subtitle={`${formatKwh(kwhSaldoCreditos)} de saldo de creditos`}
          percent={saldoCreditosRelativo}
          variant={saldoCreditosRelativo >= 30 ? 'success' : 'info'}
        />
      </section>

      {isAnnual && (
        <section className="card">
          <div className="card-header">Serie mensal do ano</div>
          <div className="card-body grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {serieMensal.map((month) => {
              const valorGeradoMes = Math.max(0, toNumber(month?.financeiro?.valor_gerado));
              const valorRecebidoMes = Math.max(0, toNumber(month?.financeiro?.valor_recebido));
              const kwhGeradoMes = Math.max(0, toNumber(month?.operacional?.kwh_gerado));
              const barWidth = (valorGeradoMes / maiorValorGeradoMensal) * 100;

              return (
                <div key={month.mes} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">{month.label}/{summary?.competencia?.ano}</p>
                    <p className="text-xs text-slate-500">Gerado</p>
                  </div>
                  <p className="text-lg font-semibold text-slate-900 mt-1">{formatMoney(valorGeradoMes)}</p>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-teal-500" style={{ width: `${clampPercent(barWidth)}%` }} />
                  </div>
                  <p className="text-xs text-emerald-700 mt-2">Recebido: {formatMoney(valorRecebidoMes)}</p>
                  <p className="text-xs text-amber-700 mt-1">kWh gerado: {formatKwh(kwhGeradoMes)}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default SolarDashboardPage;
